import React, { useContext, useEffect, useState } from 'react';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Flex,
  Form,
  Input,
  Select,
  Switch,
  Tooltip,
} from 'antd';

import {
  ASSIGNEE_TYPE_BUILDING,
  ASSIGNEE_TYPE_CREATOR,
  ASSIGNEE_TYPE_DEPARTMENT,
  ASSIGNEE_TYPE_DEPARTMENT_LEAD,
  ASSIGNEE_TYPE_EMAIL,
  ASSIGNEE_TYPE_GLOBAL,
  ASSIGNEE_TYPE_LABELS,
  ASSIGNEE_TYPE_ORGANIZATION,
  ASSIGNEE_TYPES,
  TemplateTaskAssigneeOptions,
} from '@totem/components/ticketing/templateManagement/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { TICKETS_ENDPOINT } from '@totem/utilities/endpoints';

const emptyAssigneeOptions: TemplateTaskAssigneeOptions = {
  building: [],
  organization: [],
  global: [],
  departments: [],
};

const getAssigneeValues = (
  options: TemplateTaskAssigneeOptions,
  type: string,
): string[] => {
  switch (type) {
    case ASSIGNEE_TYPE_BUILDING:
      return options.building || [];
    case ASSIGNEE_TYPE_ORGANIZATION:
      return options.organization || [];
    case ASSIGNEE_TYPE_GLOBAL:
      return options.global || [];
    case ASSIGNEE_TYPE_DEPARTMENT:
    case ASSIGNEE_TYPE_DEPARTMENT_LEAD:
      return options.departments || [];
    default:
      return [];
  }
};

const INTERNAL_EMAIL_DOMAIN = '@intelligentbuildings.com';

const isExternalEmail = (email?: string) => {
  const trimmed = (email ?? '').trim().toLowerCase();
  return trimmed.includes('@') && !trimmed.endsWith(INTERNAL_EMAIL_DOMAIN);
};

type EmailWarningProps = {
  email?: string;
  scopeOrganizationName?: string;
  unscopedReason: string;
};

// Assignment by email is a last resort: roles follow staffing changes, people don't. An outside (customer /
// vendor) address on something that isn't limited to that person's organization is worse -- they'd be added to
// other customers' tickets.
const EmailAssigneeWarning = ({
  email,
  scopeOrganizationName,
  unscopedReason,
}: EmailWarningProps) => {
  const trimmed = (email ?? '').trim();

  if (isExternalEmail(trimmed) && !scopeOrganizationName) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 12 }}
        message="Outside email address on an assignment that applies to every organization"
        description={
          <>
            <b>{trimmed}</b> is not an Intelligent Buildings address.{' '}
            {unscopedReason}, so this person would be added to matching tickets
            for <b>every customer</b> — giving them access to other
            customers&apos; tickets and information. Use an Organization or
            Building Contact role instead, which resolves to the right person
            for each customer. If you must name this person, limit the scope to
            their organization.
          </>
        }
      />
    );
  }

  if (isExternalEmail(trimmed)) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="Outside email address"
        description={
          <>
            <b>{trimmed}</b> is not an Intelligent Buildings address. Confirm
            this person belongs to <b>{scopeOrganizationName}</b>; they will be
            added to every matching ticket for that organization. An
            Organization or Building Contact role is safer and stays correct
            when people change.
          </>
        }
      />
    );
  }

  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message="Assign roles, not people"
      description="Prefer an Organization, Building or Global Contact role (e.g. Account Manager) or a Department. A specific email address keeps assigning that person until someone edits this — even after they change roles or leave."
    />
  );
};

const assigneeTypeOptions = ASSIGNEE_TYPES.map((type) => ({
  label: ASSIGNEE_TYPE_LABELS[type] ?? type,
  value: type,
}));

type Props = {
  // Form.List name (top-level form field)
  name: string;
  // Item field holding the label / department / email
  valueField: string;
  // When set, each row also picks the party type it's added as (Assignee, Watcher, ...)
  partyTypes?: string[];
  // Show a per-row "Notify" switch (stored as `notify`)
  showNotify?: boolean;
  addLabel?: string;
  // Organization the assignments are limited to; blank = they can apply to any organization's tickets
  scopeOrganizationName?: string;
  // Why an unscoped list reaches every organization, completing "...{unscopedReason}, so this person would be..."
  unscopedReason?: string;
};

/**
 * Editable list of assignee definitions, resolved to people when a ticket is created / a template is applied:
 * a contact type (organization / building / global contact label), a department or department lead, an email
 * address, or the ticket's creator. Shared by Task Templates and Ticket Assignments.
 */
const AssigneeListInput = ({
  name,
  valueField,
  partyTypes,
  showNotify = false,
  addLabel = 'Add Assignee',
  scopeOrganizationName,
  unscopedReason = 'This applies to tickets for any organization',
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const form = Form.useFormInstance();
  const [assigneeOptions, setAssigneeOptions] =
    useState<TemplateTaskAssigneeOptions>(emptyAssigneeOptions);
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    setOptionsLoading(true);

    fetch(`${TICKETS_ENDPOINT}/templates/assigneeOptions`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          return null;
        }
        return res.json();
      })
      .then((result: TemplateTaskAssigneeOptions) => {
        if (isNotNull(result)) {
          setAssigneeOptions(result);
        }
      })
      .finally(() => setOptionsLoading(false));
  }, [getApiToken]);

  const partyTypeOptions = (partyTypes ?? []).map((partyType) => ({
    label: partyType,
    value: partyType,
  }));

  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name: rowName }) => (
            <div key={key}>
              <Flex gap="small" align="baseline">
                {partyTypes && (
                  <Form.Item
                    name={[rowName, 'partyType']}
                    style={{ width: '22%' }}
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Select placeholder="Role" options={partyTypeOptions} />
                  </Form.Item>
                )}
                <Form.Item
                  name={[rowName, 'type']}
                  style={{ width: partyTypes ? '30%' : '40%' }}
                  rules={[{ required: true, message: 'Type is required.' }]}
                >
                  <Select
                    placeholder="Type"
                    options={assigneeTypeOptions}
                    onChange={() =>
                      form.setFieldValue([name, rowName, valueField], undefined)
                    }
                  />
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, current) =>
                    prev[name]?.[rowName]?.type !==
                    current[name]?.[rowName]?.type
                  }
                >
                  {({ getFieldValue }) => {
                    const assigneeType: string = getFieldValue([
                      name,
                      rowName,
                      'type',
                    ]);

                    if (assigneeType === ASSIGNEE_TYPE_CREATOR) {
                      return (
                        <Form.Item style={{ flex: 1 }}>
                          <Input
                            disabled
                            value="Ticket creator (user-created tickets only)"
                          />
                        </Form.Item>
                      );
                    }

                    const isEmail = assigneeType === ASSIGNEE_TYPE_EMAIL;
                    return (
                      <Form.Item
                        name={[rowName, valueField]}
                        style={{ flex: 1 }}
                        rules={[
                          {
                            required: true,
                            message: 'Assignee is required.',
                          },
                          ...(isEmail
                            ? [
                                {
                                  type: 'email' as const,
                                  message: 'Enter a valid email address.',
                                },
                              ]
                            : []),
                        ]}
                      >
                        {isEmail ? (
                          <Input placeholder="Email address" />
                        ) : (
                          <Select
                            showSearch
                            placeholder="Assignee"
                            disabled={!assigneeType}
                            loading={optionsLoading}
                            options={getAssigneeValues(
                              assigneeOptions,
                              assigneeType,
                            ).map((value) => ({ label: value, value }))}
                          />
                        )}
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                {showNotify && (
                  <Tooltip title="Notify">
                    <Form.Item
                      name={[rowName, 'notify']}
                      valuePropName="checked"
                    >
                      <Switch size="small" />
                    </Form.Item>
                  </Tooltip>
                )}
                <MinusCircleOutlined onClick={() => remove(rowName)} />
              </Flex>
              <Form.Item
                noStyle
                shouldUpdate={(prev, current) =>
                  prev[name]?.[rowName]?.type !==
                    current[name]?.[rowName]?.type ||
                  prev[name]?.[rowName]?.[valueField] !==
                    current[name]?.[rowName]?.[valueField]
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue([name, rowName, 'type']) ===
                  ASSIGNEE_TYPE_EMAIL ? (
                    <EmailAssigneeWarning
                      email={getFieldValue([name, rowName, valueField])}
                      scopeOrganizationName={scopeOrganizationName}
                      unscopedReason={unscopedReason}
                    />
                  ) : null
                }
              </Form.Item>
            </div>
          ))}
          <Button
            type="dashed"
            onClick={() =>
              add({
                ...(partyTypes ? { partyType: partyTypes[0] } : {}),
                ...(showNotify ? { notify: true } : {}),
                type: undefined,
                [valueField]: undefined,
              })
            }
            icon={<PlusOutlined />}
            block
          >
            {addLabel}
          </Button>
        </>
      )}
    </Form.List>
  );
};

export default AssigneeListInput;
